import { FC } from 'react';
import { DiscoverPageHeader } from './components/discover-page-header';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/ui/components/ui/tabs';
import { paths } from '../../../routes/paths';
import { useAuth } from '../../providers/authentication-provider';
import { Badge } from '@shadcn/ui/components/ui/badge';
import { PlanCode } from '@zaplify/subscriptions';
import { PageHeader } from '../../components/page-header';
import { SuggestionsPageHeader } from './suggestions/components/suggestions-page-header';
import { Sparkles } from 'lucide-react';
import { cn } from '@shadcn/ui/lib/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';

const DynamicDiscoverHeader: FC<{ activeTab: string }> = ({ activeTab }) => {
    // return <></>;
    switch (activeTab) {
        case 'suggestions':
            return <SuggestionsPageHeader />;
        case 'list-import':
            return (
                <PageHeader
                    title="List Import"
                    description="Import contacts from a CSV or Excel file to add them to Andsend"
                />
            );
        case 'filter-search':
            return <PageHeader title="Search" description="Extensive search across 500M+ contacts" />;
        default:
            return <SuggestionsPageHeader />;
    }
};

export const DiscoverPage1: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        authState: {
            tokenResult: {
                claims: { plan },
            },
        },
    } = useAuth();

    const shouldShowFilterSearch = false;
    const canAccessListImport = plan !== PlanCode.BASIC;
    console.log({ plan, canAccessListImport });

    // Determine active tab based on current path
    const getActiveTab = () => {
        if (location.pathname.includes('/import/list')) {
            return 'list-import';
        } else if (location.pathname.includes('/search')) {
            return 'filter-search';
        } else {
            return 'suggestions';
        }
    };

    const activeTab = getActiveTab();

    const handleTabChange = (value: string) => {
        // Prevent navigation to list import if on free plan
        if (value === 'list-import' && !canAccessListImport) {
            return;
        }

        if (value === 'list-import') {
            navigate(paths.NEW.IMPORT_PATHS.LIST);
        } else if (value === 'filter-search') {
            navigate(paths.NEW.DISCOVER_PATHS.SEARCH);
        } else {
            navigate(paths.NEW.DISCOVER_PATHS.SUGGESTIONS);
        }
    };

    return (
        <div className="flex max-h-[calc(100vh-3.5rem)] md:max-h-full h-full max-w-full w-full overflow-hidden">
            <div className="flex flex-col min-w-0 min-h-0 h-full max-h-full w-full overflow-hidden gap-2 p-4">
                <DynamicDiscoverHeader activeTab={activeTab} />

                <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
                    <div className="py-4 pt-0">
                        <TabsList
                            className={cn(
                                'grid w-fit gap-1 bg-transparent p-0 h-auto mx-auto',
                                shouldShowFilterSearch ? 'grid-cols-3' : 'grid-cols-2'
                            )}
                        >
                            <TabsTrigger
                                value="suggestions"
                                className="py-2 data-[state=active]:bg-background-tertiary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Suggestions
                            </TabsTrigger>
                            <TabsTrigger
                                value="list-import"
                                disabled={!canAccessListImport}
                                className="py-2 relative data-[state=active]:bg-background-tertiary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                            >
                                Import
                                {!canAccessListImport && (
                                    <Badge
                                        variant="secondary"
                                        className="ml-2 text-xs py-0.5 px-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                    >
                                        PRO
                                    </Badge>
                                )}
                            </TabsTrigger>
                            {shouldShowFilterSearch && (
                                <TabsTrigger
                                    value="filter-search"
                                    className="data-[state=active]:bg-background-tertiary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                                >
                                    Filter search
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    <TabsContent value="suggestions" className="flex-1 min-h-0 overflow-auto mt-0">
                        <Outlet />
                    </TabsContent>

                    <TabsContent value="list-import" className="flex-1 min-h-0 overflow-hidden mt-0">
                        <Outlet />
                    </TabsContent>

                    {shouldShowFilterSearch && (
                        <TabsContent value="filter-search" className="flex-1 min-h-0 overflow-hidden mt-0">
                            <Outlet />
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        </div>
    );
};

export const DiscoverPage2: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        authState: {
            tokenResult: {
                claims: { plan },
            },
        },
    } = useAuth();

    const shouldShowFilterSearch = false;
    const canAccessListImport = plan !== PlanCode.FREE;
    console.log({ plan, canAccessListImport });

    // Determine active tab based on current path
    const getActiveTab = () => {
        if (location.pathname.includes('/import/list')) {
            return 'list-import';
        } else if (location.pathname.includes('/search')) {
            return 'filter-search';
        } else {
            return 'suggestions';
        }
    };

    const activeTab = getActiveTab();

    const handleTabChange = (value: string) => {
        // Prevent navigation to list import if on free plan
        if (value === 'list-import' && !canAccessListImport) {
            return;
        }

        if (value === 'list-import') {
            navigate(paths.NEW.IMPORT_PATHS.LIST);
        } else if (value === 'filter-search') {
            navigate(paths.NEW.DISCOVER_PATHS.SEARCH);
        } else {
            navigate(paths.NEW.DISCOVER_PATHS.SUGGESTIONS);
        }
    };

    return (
        <div className="flex max-h-[calc(100vh-3.5rem)] md:max-h-full h-full max-w-full w-full overflow-hidden">
            <div className="flex flex-col min-w-0 min-h-0 h-full max-h-full w-full overflow-hidden gap-2 p-4">
                <DynamicDiscoverHeader activeTab={activeTab} />

                <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
                    <div className="py-4 pt-0">
                        <TabsList
                            className={cn(
                                'grid w-fit gap-1 bg-muted p-1 h-auto rounded-md mx-auto',
                                shouldShowFilterSearch ? 'grid-cols-3' : 'grid-cols-2'
                            )}
                        >
                            <TabsTrigger
                                value="suggestions"
                                className="py-2 px-3 text-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Suggestions
                            </TabsTrigger>
                            <TabsTrigger
                                value="list-import"
                                disabled={!canAccessListImport}
                                className="py-2 px-3 text-sm relative data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                            >
                                Import
                                {!canAccessListImport && (
                                    <Badge
                                        variant="secondary"
                                        className="ml-2 text-xs py-0.5 px-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                    >
                                        PRO
                                    </Badge>
                                )}
                            </TabsTrigger>
                            {shouldShowFilterSearch && (
                                <TabsTrigger
                                    value="filter-search"
                                    className="py-2 px-3 text-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                                >
                                    Filter search
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    <TabsContent value="suggestions" className="flex-1 min-h-0 overflow-auto mt-0">
                        <Outlet />
                    </TabsContent>

                    <TabsContent value="list-import" className="flex-1 min-h-0 overflow-hidden mt-0">
                        <Outlet />
                    </TabsContent>

                    {shouldShowFilterSearch && (
                        <TabsContent value="filter-search" className="flex-1 min-h-0 overflow-hidden mt-0">
                            <Outlet />
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        </div>
    );
};

export const DiscoverPage3: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { shadcnSearchEnabled } = useFlags();
    const {
        authState: {
            tokenResult: {
                claims: { plan },
            },
        },
    } = useAuth();

    const centerAlign = searchParams.get('centerAlign') === 'true';
    const shouldShowListImport = searchParams.get('showListImport') === 'true';
    const shouldShowFilterSearch = shadcnSearchEnabled;
    const canAccessListImport = plan !== PlanCode.FREE;
    console.log({ plan, canAccessListImport, shouldShowListImport, shouldShowFilterSearch, centerAlign });

    // Determine active tab based on current path
    const getActiveTab = () => {
        if (location.pathname.includes('/import/list')) {
            return 'list-import';
        } else if (location.pathname.includes('/search')) {
            return 'filter-search';
        } else {
            return 'suggestions';
        }
    };

    const activeTab = getActiveTab();

    const handleTabChange = (value: string) => {
        // Prevent navigation to list import if on free plan
        // if (value === 'list-import' && !canAccessListImport) {
        //     return;
        // }

        if (value === 'list-import') {
            navigate(paths.NEW.IMPORT_PATHS.LIST);
        } else if (value === 'filter-search') {
            navigate(paths.NEW.DISCOVER_PATHS.SEARCH);
        } else {
            navigate(paths.NEW.DISCOVER_PATHS.SUGGESTIONS);
        }
    };

    const shouldShowTabs = shouldShowFilterSearch || shouldShowListImport;

    return (
        <div className="flex max-h-[calc(100vh-3.5rem)] md:max-h-full h-full max-w-full w-full overflow-hidden">
            <div className="flex flex-col min-w-0 min-h-0 h-full max-h-full w-full overflow-hidden gap-2 p-4">
                <DynamicDiscoverHeader activeTab={activeTab} />

                {shouldShowTabs ? (
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
                        <div className="py-4 pt-0">
                            <TabsList
                                className={cn(
                                    `grid w-fit gap-1 bg-muted p-1 h-auto rounded-md${centerAlign ? ' mx-auto' : ''}`,
                                    `grid-cols-${1 + (shouldShowListImport ? 1 : 0) + (shouldShowFilterSearch ? 1 : 0)}`
                                )}
                            >
                                <TabsTrigger
                                    value="suggestions"
                                    className="py-2 px-3 text-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                                >
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Suggestions
                                </TabsTrigger>
                                {shouldShowListImport && (
                                    <TabsTrigger
                                        value="list-import"
                                        disabled={!canAccessListImport}
                                        className="py-2 px-3 text-sm relative data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                                    >
                                        Import
                                        {!canAccessListImport && (
                                            <Badge
                                                variant="secondary"
                                                className="ml-2 text-xs py-0.5 px-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                            >
                                                PRO
                                            </Badge>
                                        )}
                                    </TabsTrigger>
                                )}
                                {shouldShowFilterSearch && (
                                    <TabsTrigger
                                        value="filter-search"
                                        className="py-2 px-3 text-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground transition-all"
                                    >
                                        Filter search
                                    </TabsTrigger>
                                )}
                            </TabsList>
                        </div>

                        <TabsContent value="suggestions" className="flex-1 min-h-0 overflow-auto mt-0">
                            <Outlet />
                        </TabsContent>

                        <TabsContent value="list-import" className="flex-1 min-h-0 overflow-hidden mt-0">
                            <Outlet />
                        </TabsContent>

                        {shouldShowFilterSearch && (
                            <TabsContent value="filter-search" className="flex-1 min-h-0 overflow-hidden mt-0">
                                <Outlet />
                            </TabsContent>
                        )}
                    </Tabs>
                ) : (
                    <div className="flex-1 min-h-0 overflow-hidden">
                        <Outlet />
                    </div>
                )}
            </div>
        </div>
    );
};
